import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Show, UserButton, useClerk } from "@clerk/react";
import { ArrowLeft, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import IdleSignOut from "@/components/admin/IdleSignOut";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminApi } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import BookingsTab from "@/components/admin/BookingsTab";
import DailySummaryTab from "@/components/admin/DailySummaryTab";
import MonthlyReportTab from "@/components/admin/MonthlyReportTab";
import ScheduleTab from "@/components/admin/ScheduleTab";
import ContentTab from "@/components/admin/ContentTab";
import ArticlesTab from "@/components/admin/ArticlesTab";
import MediaTab from "@/components/admin/MediaTab";
import CalendarTab from "@/components/admin/CalendarTab";
import AdminsTab from "@/components/admin/AdminsTab";
import ForumTab from "@/components/admin/ForumTab";

const TABS = [
  ["bookings", "Bookings", BookingsTab],
  ["summary", "Daily summary", DailySummaryTab],
  ["monthly", "Monthly report", MonthlyReportTab],
  ["schedule", "Slots & holidays", ScheduleTab],
  ["content", "Doctor, fees & texts", ContentTab],
  ["articles", "Mental health info", ArticlesTab],
  ["media", "Logo, photo, QR, video & sound", MediaTab],
  ["calendar", "Google Calendar", CalendarTab],
  ["forum", "Mental Health Forum", ForumTab],
  ["admins", "Admins", AdminsTab],
];

function Dashboard() {
  const call = useAdminApi();
  const { reloadSettings } = useApp();
  const [role, setRole] = useState(null);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    call("get", "/me").then((m) => setRole(m.role)).catch(() => setRole("error"));
  }, [call]);

  useEffect(() => {
    if (role === "admin") call("get", "/admin/settings").then(setDraft);
  }, [role, call]);

  if (!role) return <div className="glass mx-auto mt-10 h-40 max-w-4xl animate-pulse rounded-3xl" />;
  if (role !== "admin")
    return (
      <div className="glass mx-auto mt-10 max-w-lg rounded-3xl p-8 text-center text-emerald-50" data-testid="admin-no-access">
        This account does not have admin access.
      </div>
    );

  const save = async (next) => {
    const { media, cashfree_enabled, cashfree_mode, ...body } = next;
    await call("put", "/admin/settings", body);
    setDraft(next);
    reloadSettings();
  };

  return (
    <Tabs defaultValue="bookings" className="mx-auto w-full max-w-6xl">
      <TabsList className="glass flex h-auto flex-wrap justify-start gap-1 rounded-2xl p-1.5">
        {TABS.map(([id, label]) => (
          <TabsTrigger key={id} value={id} data-testid={`admin-tab-${id}`} className="rounded-xl px-3 py-2 text-xs text-emerald-100 data-[state=active]:bg-emerald-400 data-[state=active]:text-emerald-950 sm:text-sm">
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
      {TABS.map(([id, , Comp]) => (
        <TabsContent key={id} value={id} className="mt-4">
          <div className="glass rounded-3xl p-5 text-emerald-50 sm:p-8">
            {draft ? <Comp draft={draft} save={save} /> : <div className="h-32 animate-pulse rounded-xl bg-white/5" />}
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
}

export default function AdminPage() {
  const { openUserProfile } = useClerk();
  return (
    <div className="relative z-10 min-h-screen px-4 pb-16 sm:px-8" data-testid="admin-page">
      <header className="mx-auto flex max-w-6xl items-center justify-between py-5">
        <Link to="/" data-testid="admin-back-to-site" className="flex items-center gap-2 text-sm text-emerald-100/80 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Joyful Minds
        </Link>
        <p className="font-display text-lg tracking-[0.2em] text-white">ADMIN</p>
        <Show when="signed-in">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" data-testid="account-password-btn" onClick={() => openUserProfile()} className="rounded-full">
              <KeyRound className="mr-2 h-4 w-4" /> Password & security
            </Button>
            <UserButton />
          </div>
          <IdleSignOut />
        </Show>
      </header>
      <Show when="signed-in"><Dashboard /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </div>
  );
}
